import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import type { PartnerOrganization } from '@reach/shared-types/workos-auth';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  FlaskConical,
  Headset,
  KeyRound,
  LayoutDashboard,
  Loader2,
  LogOut,
  Mail,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Settings,
  Shield,
  Star,
  SwatchBook,
  Target,
  UserCircle,
  Users as UsersIcon,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import reachLogo from '../assets/reach-logo-black-text.svg';
import { useAuth } from '../hooks/useAuth';
import {
  partnerApi,
  type OrganizationsResponse,
  type Partner,
} from '../lib/api';
import { appRoutes } from '@/app-routes';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  to?: string;
}

interface SidebarProps {
  activeItem: string;
  onItemSelect: (itemId: string) => void;
  partner?: Partner | null;
}

interface SidebarSection {
  id: string;
  label: string;
  items: SidebarItem[];
}

const SIDEBAR_SECTIONS: SidebarSection[] = [
  {
    id: 'data',
    label: 'Data',
    items: [
      {
        id: 'metrics',
        label: 'Overview',
        icon: <LayoutDashboard className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'tenants',
        label: 'Tenants',
        icon: <Building2 className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'users',
        label: 'Users',
        icon: <UsersIcon className="h-5 w-5" strokeWidth={1.5} />,
        to: '/dashboard/users',
      },
      {
        id: 'communications',
        label: 'Communications',
        icon: <MessageSquare className="h-5 w-5" strokeWidth={1.5} />,
        to: '/dashboard/communications',
      },
    ],
  },
  {
    id: 'features',
    label: 'Features (coming soon)',
    items: [
      {
        id: 'acquire',
        label: 'Acquire',
        icon: <Target className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'engage',
        label: 'Engage',
        icon: <Mail className="h-5 w-5" strokeWidth={1.5} />,
        to: `${appRoutes.ENGAGE_SETUP}/settings`,
      },
      {
        id: 'measure',
        label: 'Measure',
        icon: <BarChart3 className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'reputation',
        label: 'Reputation',
        icon: <Star className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'voice',
        label: 'Voice Answering',
        icon: <Headset className="h-5 w-5" strokeWidth={1.5} />,
      },
    ],
  },

  {
    id: 'developers',
    label: 'Developers',
    items: [
      {
        id: 'api-keys',
        label: 'API Keys',
        icon: <KeyRound className="h-5 w-5" strokeWidth={1.5} />,
        to: '/dashboard/settings/api',
      },
      {
        id: 'branding',
        label: 'Branding',
        icon: <SwatchBook className="h-5 w-5" strokeWidth={1.5} />,
        to: '/dashboard/settings/theme',
      },
      {
        id: 'data-integration',
        label: 'Data Integration',
        icon: <FileText className="h-5 w-5" strokeWidth={1.5} />,
        to: '/dashboard/settings/data-integration',
      },
      {
        id: 'testing',
        label: 'Testing',
        icon: <FlaskConical className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'guides',
        label: 'Guides',
        icon: <FileText className="h-5 w-5" strokeWidth={1.5} />,
      },
      {
        id: 'documentation',
        label: 'API Docs',
        icon: <BookOpen className="h-5 w-5" strokeWidth={1.5} />,
      },
    ],
  },
];

export const Sidebar: React.FC<SidebarProps> = ({
  activeItem,
  onItemSelect,
  partner,
}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
  const [switchingToOrgId, setSwitchingToOrgId] = useState<string | null>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [paginationCursor, setPaginationCursor] = useState<{
    before?: string;
    after?: string;
  }>({});
  const { user, organization, logout } = useAuth();

  const ORGS_PER_PAGE = 5;

  const {
    data: organizationsResponse,
    isLoading: isLoadingOrgs,
    isFetching: isFetchingOrgs,
  } = useQuery<OrganizationsResponse>({
    queryKey: ['organizations', paginationCursor],
    queryFn: () =>
      partnerApi.getOrganizations({
        limit: ORGS_PER_PAGE,
        ...paginationCursor,
      }),
    enabled: !!user,
    placeholderData: previousData => previousData, // Keep previous data visible while loading
  });

  // Type assertion to handle schema update - data now uses 'results' instead of 'organizations'
  const data = organizationsResponse?.data as
    | {
        results: PartnerOrganization[];
        pagination: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          cursor: string | null | undefined;
          previousCursor: string | null | undefined;
        };
      }
    | undefined;
  const organizations = data?.results || [];
  const pagination = data?.pagination;
  // Show switcher if user has multiple orgs on current page, or if there are more pages (indicating more orgs exist)
  const showOrgSwitcher =
    organizations.length > 1 ||
    !!pagination?.hasNextPage ||
    !!pagination?.hasPreviousPage;

  // Check if we're on initial load (no pagination cursor)
  const isInitialLoad = !paginationCursor.before && !paginationCursor.after;

  // Hide pagination on initial load if we have <= ORGS_PER_PAGE and no next page
  // Otherwise show pagination if there's a next or previous page
  const shouldHideOnInitialLoad =
    isInitialLoad &&
    organizations.length <= ORGS_PER_PAGE &&
    !pagination?.hasNextPage;

  const showPagination =
    !shouldHideOnInitialLoad &&
    (pagination?.hasNextPage || pagination?.hasPreviousPage);

  // Reset pagination when dropdown closes
  useEffect(() => {
    if (!isOrgSwitcherOpen) {
      setPaginationCursor({});
    }
  }, [isOrgSwitcherOpen]);

  const handleLogout = () => {
    logout();
  };

  const handleSwitchOrganization = async (orgId: string) => {
    if (orgId === organization?.id) {
      setIsOrgSwitcherOpen(false);
      return;
    }

    setSwitchingToOrgId(orgId);

    try {
      const data = await partnerApi.switchOrganization(orgId);

      if (data.data.requiresReauth) {
        window.location.href = data.data.authorizationUrl;
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      setSwitchingToOrgId(null);
    }
  };

  return (
    <Tooltip.Provider delayDuration={100}>
      <aside
        className={`bg-slate-800 border-r border-slate-700 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}>
        <div className="flex flex-col h-full">
          {/* Reach Logo Section */}
          <div className="border-b border-slate-700">
            <div className="flex items-center justify-between px-4 py-4">
              {isCollapsed ? (
                <button
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                  title="Expand sidebar">
                  <PanelRightClose className="h-4 w-4" />
                </button>
              ) : (
                <>
                  <Link
                    to="/dashboard/metrics"
                    className="flex items-center justify-start transition-colors">
                    <img
                      src={reachLogo}
                      alt="Reach"
                      className="h-4 w-auto brightness-0 invert opacity-80"
                    />
                  </Link>
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                    title="Collapse sidebar">
                    <PanelRightOpen className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Partner Info Section with Org Switcher */}
          {!isCollapsed && partner && (
            <div className="border-b border-slate-700">
              <button
                onClick={() =>
                  showOrgSwitcher && setIsOrgSwitcherOpen(!isOrgSwitcherOpen)
                }
                disabled={!showOrgSwitcher}
                className={`w-full px-4 py-5 ${
                  showOrgSwitcher ? 'hover:bg-slate-700 cursor-pointer' : ''
                } transition-colors`}>
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 bg-white">
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={`${partner.name} logo`}
                        className="h-full w-full object-contain bg-white"
                      />
                    ) : (
                      <div className="h-full w-full bg-white rounded-lg flex items-center justify-center">
                        <Shield className="h-5 w-5 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-white truncate font-nav">
                        {organization?.name || partner.name}
                      </p>
                      {showOrgSwitcher && (
                        <ChevronDown
                          className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${
                            isOrgSwitcherOpen ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </button>

              {/* Organization Dropdown */}
              {isOrgSwitcherOpen && showOrgSwitcher && (
                <div className="border-t border-slate-700 bg-slate-900">
                  {isLoadingOrgs && organizations.length === 0 ? (
                    <div className="px-4 py-3 flex items-center space-x-2 text-sm text-slate-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <>
                      <div className="max-h-60 overflow-y-auto custom-scrollbar hover-scrollbar">
                        {organizations.map((org: PartnerOrganization) => {
                          const isActive = org.id === organization?.id;
                          const isSwitching = switchingToOrgId === org.id;

                          return (
                            <button
                              key={org.id}
                              onClick={() => handleSwitchOrganization(org.id)}
                              disabled={isSwitching}
                              className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-800 transition-colors ${
                                isActive ? 'bg-slate-800' : ''
                              } ${isSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {org.name}
                                  </p>
                                  {org.domain && (
                                    <p className="text-xs text-slate-500 truncate">
                                      {org.domain}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isSwitching ? (
                                <Loader2 className="w-4 h-4 animate-spin text-slate-400 flex-shrink-0" />
                              ) : isActive ? (
                                <Check className="w-4 h-4 text-white flex-shrink-0" />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                      {/* Pagination Controls */}
                      {showPagination && (
                        <div className="border-t border-slate-700 px-4 py-2 flex items-center justify-between bg-slate-800">
                          <button
                            onClick={() => {
                              if (pagination?.previousCursor) {
                                setPaginationCursor({
                                  before: pagination.previousCursor,
                                  after: undefined,
                                });
                              }
                            }}
                            disabled={
                              !pagination?.hasPreviousPage || isFetchingOrgs
                            }
                            className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Previous page">
                            {isFetchingOrgs ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ChevronLeft className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              if (pagination?.cursor) {
                                setPaginationCursor({
                                  after: pagination.cursor,
                                  before: undefined,
                                });
                              }
                            }}
                            disabled={
                              !pagination?.hasNextPage || isFetchingOrgs
                            }
                            className="p-1.5 rounded-md hover:bg-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Next page">
                            {isFetchingOrgs ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {isCollapsed && partner && (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <div className="px-2 py-4 border-b border-slate-700 flex justify-center">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                    {partner.logoUrl ? (
                      <img
                        src={partner.logoUrl}
                        alt={`${partner.name} logo`}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="h-full w-full bg-whiterounded-lg flex items-center justify-center">
                        <Shield className="h-4 w-4 text-slate-400" />
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="right"
                  sideOffset={8}
                  className="z-50 px-3 py-1.5 text-sm font-medium text-white bg-slate-800 rounded-md shadow-md">
                  {organization?.name || partner.name}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          )}

          {/* Navigation Items by Section */}
          <nav className="flex-1 px-2 pt-4 pb-2 space-y-4 overflow-y-auto custom-scrollbar hover-scrollbar">
            {SIDEBAR_SECTIONS.map(section => (
              <div key={section.id}>
                {!isCollapsed && (
                  <p className="px-3 pb-2 text-[11px] uppercase tracking-wide text-slate-500 font-light font-nav">
                    {section.label}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map(item => (
                    <Tooltip.Root
                      key={item.id}
                      open={isCollapsed && hoveredItemId === item.id}>
                      <Tooltip.Trigger asChild>
                        <button
                          onClick={() => {
                            if (item.id === 'guides') {
                              window.open(
                                'https://docs.embedreach.com/samples/jwt-tokens',
                                '_blank',
                                'noopener,noreferrer',
                              );
                              return;
                            }
                            if (item.id === 'documentation') {
                              window.open(
                                'https://docs.embedreach.com/api-reference/introduction',
                                '_blank',
                                'noopener,noreferrer',
                              );
                              return;
                            }
                            if (item.id === 'testing') {
                              window.open(
                                'https://docs.embedreach.com/embeddable-ui/iframe/sandbox',
                                '_blank',
                                'noopener,noreferrer',
                              );
                              return;
                            }
                            if (item.to) {
                              navigate(item.to);
                            } else {
                              onItemSelect(item.id);
                            }
                          }}
                          onMouseEnter={() => {
                            if (isCollapsed) setHoveredItemId(item.id);
                          }}
                          onMouseLeave={() => {
                            if (isCollapsed) setHoveredItemId(null);
                          }}
                          className={`w-full flex items-center px-3 py-2 text-sm font-light rounded-md transition-colors ${
                            activeItem === item.id
                              ? 'bg-slate-700 text-white'
                              : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                          } ${isCollapsed ? 'justify-center' : 'justify-start'}`}>
                          {item.icon}
                          {!isCollapsed && (
                            <span className="ml-3 font-nav flex items-center gap-1">
                              <span
                                className={
                                  !item.to &&
                                  ![
                                    'metrics',
                                    'tenants',
                                    'communications',
                                    'guides',
                                    'documentation',
                                    'testing',
                                  ].includes(item.id)
                                    ? 'italic'
                                    : ''
                                }>
                                {item.label}
                              </span>
                              {['guides', 'documentation', 'testing'].includes(
                                item.id,
                              ) && (
                                <ExternalLink
                                  className="h-3.5 w-3.5 text-slate-400"
                                  strokeWidth={2}
                                />
                              )}
                            </span>
                          )}
                        </button>
                      </Tooltip.Trigger>
                      {isCollapsed && (
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="right"
                            sideOffset={8}
                            className="z-50 px-3 py-1.5 text-sm font-light text-white bg-slate-800 rounded-md shadow-md font-nav">
                            {item.label}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      )}
                    </Tooltip.Root>
                  ))}
                </div>
              </div>
            ))}

            {/* Settings moved into scrollable area */}
            <div className="pt-2">
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={() => navigate('/dashboard/settings')}
                    className={`w-full flex items-center px-3 py-2 text-sm font-light rounded-md transition-colors ${
                      activeItem === 'profile'
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    } ${isCollapsed ? 'justify-center' : 'justify-start'}`}>
                    <Settings className="h-5 w-5" strokeWidth={1.5} />
                    {!isCollapsed && (
                      <span className="ml-3 font-nav">Settings</span>
                    )}
                  </button>
                </Tooltip.Trigger>
                {isCollapsed && (
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="right"
                      sideOffset={8}
                      className="z-50 px-3 py-1.5 text-sm font-light text-white bg-slate-800 rounded-md shadow-md font-nav">
                      Settings
                    </Tooltip.Content>
                  </Tooltip.Portal>
                )}
              </Tooltip.Root>
            </div>
          </nav>

          {/* User Info Section */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className={`px-3 py-4 border-t border-slate-700 w-full hover:bg-slate-700 transition-colors ${isCollapsed ? 'flex justify-center' : ''}`}>
                {isCollapsed ? (
                  <div className="flex items-center justify-center">
                    {user?.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircle className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    {user?.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <UserCircle className="h-10 w-10 text-slate-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate font-nav">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                )}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                side="right"
                align="end"
                sideOffset={8}
                className="z-50 min-w-[200px] bg-white rounded-md shadow-lg border border-slate-200 p-1">
                <DropdownMenu.Item
                  onSelect={e => {
                    e.preventDefault();
                    handleLogout();
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md cursor-pointer outline-none">
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </aside>
    </Tooltip.Provider>
  );
};
